import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { ComposedChart, Line, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { salesCyclesApi } from '../services/api';
import DealsListModal from './DealsListModal';

type PeriodType = 'week' | 'month' | 'quarter';

interface TacticalInsight {
  display_name: string;
  criteria_name: string;
  stage_name: string;
  amount_with_criteria: number;
  amount_without_criteria: number;
  winrate_with: number;
  winrate_without: number;
  deals_with_criteria: number;
  deals_without_criteria: number;
  closed_deals_with_criteria: number;
  closed_deals_without_criteria: number;
  amount_with_criteria_deals: number;
  amount_without_criteria_deals: number;
  // NEW: Open deals without criteria (only for identified_champion)
  open_deals_without_criteria?: number;
  open_amount_without_criteria?: number;
}

interface StrategicInsight {
  primary_reason: string;
  deal_count: number;
  total_amount: number;
  avg_deal_size: number;
  avg_days_to_close: number;
  percentage_of_deals: number;
  percentage_of_amount: number;
}

interface InsightsTabProps {
  selectedCycleTypes?: string[];
  selectedAccountValues?: string[];
  selectedSalesRep?: string | null;
  selectedTeam?: string | null;
  hideHeader?: boolean;
  isFetching?: boolean; // New prop for subtle loading during filter changes
  // Data props (passed from parent page)
  tacticalData?: {
    tactical_insights: TacticalInsight[];
    summary: {
      total_criteria: number;
      total_deals_analyzed: number;
    };
  };
  strategicData?: {
    strategic_insights: StrategicInsight[];
    total_deals: number;
    categorized_deals: number;
    summary: {
      total_deals: number;
      total_amount: number;
      top_3_reasons: string[];
    };
  };
  isLoading?: boolean;
  hasError?: boolean;
}

const InsightsTab: React.FC<InsightsTabProps> = ({
  selectedCycleTypes,
  selectedAccountValues,
  selectedSalesRep,
  selectedTeam,
  hideHeader = false,
  isFetching: externalIsFetching,
  tacticalData,
  strategicData,
  isLoading,
  hasError
}) => {
  const [activeSection, setActiveSection] = useState<'champions' | 'tactical' | 'strategic'>('champions');
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('week');

  // Period options for the selector
  const periodOptions = [
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' },
    { value: 'quarter', label: 'Quarterly' }
  ] as const;

  // Modal state for strategic insights deals
  const [isDealsModalOpen, setIsDealsModalOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [selectedReasonDisplay, setSelectedReasonDisplay] = useState<string | null>(null);

  // Modal state for tactical insights deals
  const [isTacticalDealsModalOpen, setIsTacticalDealsModalOpen] = useState(false);
  const [selectedCriteria, setSelectedCriteria] = useState<string | null>(null);
  const [selectedCriteriaDisplay, setSelectedCriteriaDisplay] = useState<string | null>(null);

  // Data is now fetched at the page level and passed down as props
  // This component now receives data and loading states from parent
  const isFetching = externalIsFetching;

  // Debug modal state changes
  React.useEffect(() => {
    console.log('Tactical modal state changed:', { isTacticalDealsModalOpen, selectedCriteria, selectedCriteriaDisplay });
  }, [isTacticalDealsModalOpen, selectedCriteria, selectedCriteriaDisplay]);

  // Convert cycle types array to comma-separated string for API calls
  const cycleTypesString = selectedCycleTypes && selectedCycleTypes.length > 0 ? selectedCycleTypes.join(',') : undefined;

  // Fetch MM+ decision makers trends data for period-over-period metric
  const { data: decisionMakersTrends, isLoading: isTrendsLoading, error: trendsError } = useQuery(
    [
      'decisionMakersTrendsMMPlus',
      selectedPeriod, // Use selected period for period-over-period comparison
      cycleTypesString || 'all',
      selectedSalesRep,
      selectedTeam
    ],
    () => salesCyclesApi.getDecisionMakersTrendsMMPlus(
      selectedPeriod,
      cycleTypesString,
      selectedSalesRep,
      selectedTeam
    ),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      enabled: !isLoading && !hasError && activeSection === 'champions', // Only fetch when champions tab is active
      retry: selectedPeriod === 'quarter' ? 1 : 3, // Less retries for quarterly to fail fast
      retryDelay: 1000,
      onError: (error: any) => {
        console.error('Error fetching decision makers trends:', {
          period: selectedPeriod,
          error: error,
          message: error?.message || 'Unknown error',
          response: error?.response || 'No response data',
          stack: error?.stack
        });
      }
    }
  );

  // Calculate period-over-period change for champion rate
  const getDecisionMakersPeriodOverPeriod = () => {
    if (!decisionMakersTrends || decisionMakersTrends.length < 2) {
      return { currentPeriod: 0, previousPeriod: 0, change: 0, changePercentage: 0 };
    }

    // Get the last two periods of data (most recent first)
    const sortedData = [...decisionMakersTrends].sort((a, b) =>
      new Date(b.period_start).getTime() - new Date(a.period_start).getTime()
    );

    const currentPeriod = sortedData[0]?.champion_percentage || 0;
    const previousPeriod = sortedData[1]?.champion_percentage || 0;
    const change = currentPeriod - previousPeriod;
    const changePercentage = previousPeriod > 0 ? ((change / previousPeriod) * 100) : 0;

    return { currentPeriod, previousPeriod, change, changePercentage };
  };

  const periodOverPeriodData = getDecisionMakersPeriodOverPeriod();

  // Debug logging
  React.useEffect(() => {
    console.log('Decision makers trends debug:', {
      selectedPeriod,
      data: decisionMakersTrends,
      dataLength: decisionMakersTrends?.length,
      isLoading: isTrendsLoading,
      error: trendsError,
      errorMessage: (trendsError as any)?.message || 'No error message',
      activeSection,
      enabled: !isLoading && !hasError && activeSection === 'champions'
    });
  }, [decisionMakersTrends, selectedPeriod, isTrendsLoading, trendsError, activeSection, isLoading, hasError]);

  // Handle strategic insight card click
  const handleStrategicInsightClick = (reason: string) => {
    setSelectedReason(reason);
    setSelectedReasonDisplay(reason);
    setIsDealsModalOpen(true);
  };

  // Handle tactical insight criteria click
  const handleTacticalCriteriaClick = (criteriaName: string, displayName: string) => {
    console.log('Tactical criteria clicked:', { criteriaName, displayName });
    setSelectedCriteria(criteriaName);
    setSelectedCriteriaDisplay(displayName);
    setIsTacticalDealsModalOpen(true);
    console.log('Modal state set to open');
    console.log('Current modal state:', { isTacticalDealsModalOpen: true, selectedCriteria: criteriaName, selectedCriteriaDisplay: displayName });
  };

  // Close modal
  const closeDealsModal = () => {
    setIsDealsModalOpen(false);
    setSelectedReason(null);
    setSelectedReasonDisplay(null);
  };

  const closeTacticalDealsModal = () => {
    setIsTacticalDealsModalOpen(false);
    setSelectedCriteria(null);
    setSelectedCriteriaDisplay(null);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        <div className="text-gray-700 dark:text-gray-300 text-sm font-inter font-medium">Loading insights...</div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
        <div className="text-center">
          <div className="text-red-600 dark:text-red-400 text-lg font-semibold font-inter mb-2">Error loading insights</div>
          <div className="text-gray-600 dark:text-gray-400 text-sm font-inter">Please try again later</div>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {!hideHeader && (
        <div className="flex items-start justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 font-plus-jakarta">Sales Insights</h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm font-inter mt-2">
            Actionable recommendations to increase win rates and optimize your sales process
          </p>
        </div>
        </div>
      )}


      {/* Main Content Window with Tabs Inside */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm">
        {/* Navigation Tabs - Now Inside the White Window */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex space-x-1 bg-gray-50 dark:bg-gray-700 rounded-lg p-1">
            {[
              { key: 'champions', label: 'Deals with Identified Champions' },
              { key: 'tactical', label: 'Tactical Insights' },
              { key: 'strategic', label: 'Strategic Insights' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveSection(tab.key as any)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors font-inter ${
                  activeSection === tab.key
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Sections */}
        {activeSection === 'champions' && (
          <div className="px-6 pb-6 relative">
            {/* Header with Period Selector */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="flex items-center space-x-3">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 font-plus-jakarta">MM+ Deals with Identified Champions</h3>
                  {isTrendsLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                  )}
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm font-inter mt-1">
                  Champion identification trends for Midmarket, Enterprise, and Strategic accounts • {decisionMakersTrends?.length || 0} periods shown
                </p>
              </div>

              {/* Period Selector */}
              <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                {periodOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSelectedPeriod(option.value)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors font-inter ${
                      selectedPeriod === option.value
                        ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Chart */}
            <div className="h-96">
              {decisionMakersTrends && decisionMakersTrends.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={decisionMakersTrends}
                    margin={{
                      top: 20,
                      right: 50,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis
                      dataKey="period_label"
                      stroke="#64748b"
                      fontSize={12}
                      fontFamily="Inter, sans-serif"
                      tick={{ fill: '#64748b' }}
                    />
                    {/* Left Y-axis for Champion Rate */}
                    <YAxis
                      yAxisId="champion"
                      stroke="#64748b"
                      fontSize={12}
                      fontFamily="Inter, sans-serif"
                      tick={{ fill: '#64748b' }}
                      domain={[0, 100]}
                      tickCount={6}
                    />
                    {/* Right Y-axis for Deal Count */}
                    <YAxis
                      yAxisId="deals"
                      orientation="right"
                      stroke="#64748b"
                      fontSize={12}
                      fontFamily="Inter, sans-serif"
                      tick={{ fill: '#64748b' }}
                      domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.1)]}
                      tickCount={6}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length > 0) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white dark:bg-gray-800 p-4 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                              <p className="text-gray-900 dark:text-gray-100 font-semibold font-inter">{label}</p>
                              <div className="space-y-2 mt-2">
                                <div className="flex items-center space-x-2">
                                  <div className="w-3 h-3 bg-purple-600 rounded-full"></div>
                                  <span className="text-sm text-gray-600 dark:text-gray-400 font-inter">Champion Rate: </span>
                                  <span className="text-sm font-semibold text-purple-600 font-inter">{data.champion_percentage}%</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                                  <span className="text-sm text-gray-600 dark:text-gray-400 font-inter">MM+ Deals: </span>
                                  <span className="text-sm font-semibold text-gray-600 dark:text-gray-300 font-inter">{formatNumber(data.total_deals)}</span>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1 font-inter">
                                  <div>Champion Value: {formatCurrency(data.amount_with_champions)}</div>
                                  <div>Total Value: {formatCurrency(data.total_amount)}</div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend
                      wrapperStyle={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}
                    />
                    {/* Primary line: Champion Rate */}
                    <Line
                      yAxisId="champion"
                      type="monotone"
                      dataKey="champion_percentage"
                      stroke="#9333ea"
                      strokeWidth={3}
                      dot={{ fill: '#9333ea', strokeWidth: 2, r: 5 }}
                      activeDot={{ r: 7, stroke: '#9333ea', strokeWidth: 2, fill: '#ffffff' }}
                      name="Champion Rate (%)"
                      connectNulls={false}
                    />
                    {/* Secondary line: MM+ Deal Count */}
                    <Line
                      yAxisId="deals"
                      type="monotone"
                      dataKey="total_deals"
                      stroke="#9ca3af"
                      strokeWidth={1.5}
                      strokeDasharray="5 5"
                      dot={{ fill: '#9ca3af', strokeWidth: 1, r: 3 }}
                      activeDot={{ r: 5, stroke: '#9ca3af', strokeWidth: 1, fill: '#ffffff' }}
                      name="MM+ Deals Count"
                      connectNulls={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center min-h-[400px]">
                  <div className="text-center">
                    <div className="text-gray-900 dark:text-gray-100 text-lg font-semibold font-inter mb-2">
                      {isTrendsLoading ? 'Loading trends data...' : (trendsError ? `Error loading ${selectedPeriod}ly trends` : 'No trends data available')}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400 text-sm font-inter">
                      {isTrendsLoading ? '' : (trendsError ? `Unable to load ${selectedPeriod}ly data. Please try a different time period or check console for details.` : `Try adjusting your filters or switching to ${selectedPeriod === 'quarter' ? 'monthly' : 'weekly'} view`)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeSection === 'tactical' && (
          <div className="px-6 pb-6 relative">
          <div className="mb-6">
            <p className="text-gray-600 dark:text-gray-400 text-sm font-inter">
              Exit criteria analysis showing win rates with and without each criterion
            </p>
          </div>

          {tacticalData?.tactical_insights && tacticalData.tactical_insights.length > 0 ? (
            <div className="space-y-4">
              {/* Single Tactical Insights Table */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 max-w-full">
                <div className="overflow-x-auto">
                  <table className="w-full table-fixed">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-2 text-gray-900 dark:text-gray-100 font-semibold font-inter w-[30%]">Exit Criteria</th>
                        <th className="text-center py-3 px-2 text-gray-900 dark:text-gray-100 font-semibold font-inter w-[17%]">Win Rate With</th>
                        <th className="text-center py-3 px-2 text-gray-900 dark:text-gray-100 font-semibold font-inter w-[17%]">Win Rate Without</th>
                        <th className="text-center py-3 px-2 text-gray-900 dark:text-gray-100 font-semibold font-inter w-[18%]">Open Deals Without</th>
                        <th className="text-center py-3 px-2 text-gray-900 dark:text-gray-100 font-semibold font-inter w-[18%]">Amount of Open Deals Without</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tacticalData.tactical_insights.map((insight) => (
                        <tr 
                          key={insight.criteria_name} 
                          className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                          onClick={() => handleTacticalCriteriaClick(insight.criteria_name, insight.display_name)}
                        >
                          <td className="py-3 px-2">
                            <div className="text-gray-900 dark:text-gray-100 font-medium font-inter truncate">{insight.display_name}</div>
                            <div className="text-xs text-gray-400 font-inter mt-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                {insight.stage_name}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className="px-2 py-1 rounded text-sm font-medium bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-inter">
                              {insight.winrate_with}%
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className="px-2 py-1 rounded text-sm font-medium bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-inter">
                              {insight.winrate_without}%
                            </span>
                          </td>
                            <td className="py-3 px-2 text-center">
                              <span className="px-2 py-1 rounded text-sm font-medium text-gray-900 dark:text-gray-100 font-inter">
                                {insight.open_deals_without_criteria || 0}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-center">
                              <span className="px-2 py-1 rounded text-sm font-medium text-gray-900 dark:text-gray-100 font-inter">
                                ${((insight.open_amount_without_criteria || 0) / 1000).toFixed(0)}K
                              </span>
                            </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="text-gray-700 dark:text-gray-300 text-lg font-inter">No tactical insights available</div>
              <div className="text-gray-500 dark:text-gray-400 text-sm font-inter mt-2">Try adjusting your filters</div>
            </div>
          )}
        </div>
      )}

      {activeSection === 'strategic' && (
        <div className="px-6 pb-6 relative">
          <div className="mb-6">
            <p className="text-gray-600 dark:text-gray-400 text-sm font-inter">
              Primary loss reasons from closed-lost analysis with deal impact
            </p>
          </div>

          {strategicData?.strategic_insights && strategicData.strategic_insights.length > 0 ? (
            <div className="space-y-4">
              {/* Summary with categorized deals note - moved to top left corner */}
              <div className="flex justify-start mb-2">
                <div className="text-xs text-gray-500 dark:text-gray-400 font-inter">
                  {formatNumber(strategicData.total_deals)} total deals → {formatNumber(strategicData.categorized_deals)} categorized deals
                </div>
              </div>

              {/* Strategic Insights List */}
              <div className="space-y-3">
                {strategicData.strategic_insights.map((insight, index) => {
                  const isTop3 = index < 3;
                  return (
                    <div
                      key={insight.primary_reason}
                      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                      onClick={() => handleStrategicInsightClick(insight.primary_reason)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate font-inter">
                              {insight.primary_reason}
                            </h4>
                            {isTop3 && (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-inter">
                                Top {index + 1}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-6 text-sm text-gray-600 dark:text-gray-400 mt-2">
                            <div className="flex items-center space-x-1">
                              <span className="text-gray-500 dark:text-gray-400">Deals:</span>
                              <span className="font-medium">{formatNumber(insight.deal_count)}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className="text-gray-500 dark:text-gray-400">Percentage:</span>
                              <span className="font-medium">{insight.percentage_of_deals.toFixed(1)}%</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center ml-4">
                          <div className="text-right">
                            <div className="text-gray-900 dark:text-gray-100 text-lg font-bold font-inter">
                              {formatCurrency(insight.total_amount)}
                            </div>
                            <div className="text-gray-500 dark:text-gray-400 text-xs font-inter">ARR lost</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="text-gray-700 dark:text-gray-300 text-lg font-inter">No strategic insights available</div>
              <div className="text-gray-500 dark:text-gray-400 text-sm font-inter mt-2">Try adjusting your filters</div>
            </div>
          )}
        </div>
      )}
      </div> {/* Close main white window */}

      {/* Strategic Insights Deals Modal */}
      <DealsListModal
        isOpen={isDealsModalOpen}
        onClose={closeDealsModal}
        stageName={`Deals with "${selectedReasonDisplay || selectedReason || 'Unknown Reason'}"`}
        icon="📊"
        dealsCount={0} // Will be handled by the API response
        modalType="strategic-insights"
        reason={selectedReason || undefined}
        selectedCycleTypes={selectedCycleTypes}
        selectedAccountValues={selectedAccountValues}
        selectedSalesRep={selectedSalesRep}
        selectedTeam={selectedTeam}
      />

      {/* Tactical Insights Deals Modal */}
      <DealsListModal
        isOpen={isTacticalDealsModalOpen}
        onClose={closeTacticalDealsModal}
        stageName={`Deals with "${selectedCriteriaDisplay || selectedCriteria || 'Unknown Criteria'}"`}
        icon="🎯"
        dealsCount={0} // Will be handled by the API response
        modalType="tactical-insights"
        criteria={selectedCriteria || undefined}
        selectedCycleTypes={selectedCycleTypes}
        selectedAccountValues={selectedAccountValues}
        selectedSalesRep={selectedSalesRep}
        selectedTeam={selectedTeam}
      />
    </div>
  );
};

export default InsightsTab;